import { create } from "zustand";
import { persist } from "zustand/middleware";
import { setStoredAccessToken } from "@/lib/auth-token-storage";
import { forgetPendingInvite } from "@/lib/pending-invite";
import { clearSessionActivity, touchSessionActivity } from "@/lib/session-activity";

export type AuthUser = {
  id: string;
  email: string;
  name: string | null;
  tenantId: string;
  tenantSlug: string;
  role: string;
  /** H4: the Terms of use + Privacy notice still have to be accepted in this school. */
  termsRequired?: boolean;
};

type AuthState = {
  accessToken: string | null;
  user: AuthUser | null;
  isAuthenticated: boolean;
  hasHydrated: boolean;
  /** A sign-in (password, OTP, Google, tenant switch, register): always a new idle clock. */
  setSession: (accessToken: string, user: AuthUser) => void;
  /** A token refresh: continues the signed-in session without counting as activity. */
  renewSession: (accessToken: string, user: AuthUser) => void;
  clearSession: () => void;
  setHasHydrated: (hasHydrated: boolean) => void;
};

/**
 * SPA-8: bumped by every sign-in and sign-out (not by a token renewal). A refresh that started
 * under an older epoch must not store its answer: the user signed out (or in as someone else)
 * while it was in flight.
 */
let sessionEpoch = 0;

export function currentSessionEpoch(): number {
  return sessionEpoch;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      accessToken: null,
      user: null,
      isAuthenticated: false,
      hasHydrated: false,
      setSession: (accessToken, user) => {
        sessionEpoch += 1;
        setStoredAccessToken(accessToken);
        // SPA-1 / F6: a sign-in starts a fresh idle clock, so a last-activity time left from an
        // earlier session can't sign it straight back out — even when this browser still holds
        // an old session for the same user (e.g. Google sign-in the next morning).
        touchSessionActivity(Date.now(), { force: true });
        set({ accessToken, user, isAuthenticated: true });
      },
      renewSession: (accessToken, user) => {
        setStoredAccessToken(accessToken);
        // A token refresh for the same signed-in user is not activity and leaves the clock alone;
        // anything else is treated like a sign-in.
        const prev = get();
        const sameSession =
          prev.isAuthenticated &&
          prev.user?.id === user.id &&
          prev.user?.tenantId === user.tenantId;
        if (!sameSession) touchSessionActivity(Date.now(), { force: true });
        set({ accessToken, user, isAuthenticated: true });
      },
      clearSession: () => {
        sessionEpoch += 1;
        setStoredAccessToken(null);
        // R3-S5 / SEC-8: every sign-out path (menu, privacy, idle, expired refresh) forgets a
        // remembered invite, so the next person to use this browser doesn't inherit it.
        forgetPendingInvite();
        // SPA-1: every sign-out path (menu, privacy, idle, other tab, invite switch, expired
        // refresh) forgets the last-activity time too.
        clearSessionActivity();
        set({ accessToken: null, user: null, isAuthenticated: false });
      },
      setHasHydrated: (hasHydrated) => set({ hasHydrated }),
    }),
    {
      name: "lms-auth",
      partialize: (state) => ({
        accessToken: state.accessToken,
        user: state.user,
        isAuthenticated: state.isAuthenticated,
      }),
      onRehydrateStorage: () => (state) => {
        if (state?.accessToken) {
          setStoredAccessToken(state.accessToken);
        }
      },
    },
  ),
);

useAuthStore.persist.onFinishHydration(() => {
  useAuthStore.setState({ hasHydrated: true });
});

if (useAuthStore.persist.hasHydrated()) {
  useAuthStore.setState({ hasHydrated: true });
}
